import {
  actionsKeys,
  getRequiredFields,
  permissionActions,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import {
  BUDGET_ID,
  BUDGET_NAME,
  LAST_UPDATED_BY,
  LAST_UPDATED_ON,
  LINE_ITEMS_COUNT,
  NOTES,
  TOTAL_ANNUAL_BUDGET,
  TOTAL_APPROVALS,
  VERSION_NO,
  YEAR,
} from "./consts";
import { schema } from "./validation";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useAddBudgetMutation,
  useGetBomUsersList,
  useGetBudgetById,
  useGetCommentsByBudgetId,
  useGetCommunityApprovalInfo,
  useSubmitForApprovalMutation,
  useUpdateBudgetMutation,
} from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";

const useAddEditBudget = () => {
  const { budgetId, action } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isView = budgetId && action == "view";
  const isAdd = !budgetId;
  const [yearKey, setYearKey] = useState<number>(Math.random() * 4);
  const [versionKey, setVersionKey] = useState<number>(Math.random() * 2);
  const [openApprovalDialog, setOpenApprovalDialog] = useState<boolean>(false);
  const [approvalClicked, setApprovalClicked] = useState<boolean>(false);
  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const communityId = communityDetail?.communityId;
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );
  const budgetPermission = accessPermission?.[actionsKeys.BUDGET.toLowerCase()];
  const hasAddPermission = !!budgetPermission?.[permissionActions.CREATE];
  const hasUpdatePermission = !!budgetPermission?.[permissionActions.UPDATE];
  const hasSubmitPermission = !!budgetPermission?.[permissionActions.SUBMIT];

  const form = useForm<any>({
    defaultValues: {
      [BUDGET_ID]: "",
      [BUDGET_NAME]: "",
      [VERSION_NO]: "",
      [YEAR]: "",
      [TOTAL_ANNUAL_BUDGET]: "0",
      [TOTAL_APPROVALS]: "",
      [NOTES]: "",
      [LAST_UPDATED_BY]: "",
      [LAST_UPDATED_ON]: "",
      [LINE_ITEMS_COUNT]: "",
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);
  const formData = useWatch({ control: form.control });

  const { data: budgetData, isLoading: isBudgetDataLoading } = useGetBudgetById(
    {
      communityId: communityId as string,
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!(communityId && budgetId),
      },
    }
  );

  const { data: commentsData, isLoading: isCommentsLoading } =
    useGetCommentsByBudgetId({
      communityId: communityId as string,
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!(communityId && budgetId),
      },
    });

  const { data: bomUsersList } = useGetBomUsersList({
    communityId: communityId as string,
    queryParams: {
      enabled: !!communityId,
    },
  });

  const { data: communityApprovalInfo } = useGetCommunityApprovalInfo({
    community_id: communityId as string,
    queryParams: {
      enabled: !!communityId,
    },
  });

  useEffect(() => {
    if (budgetData) {
      if (
        (budgetData?.data?.status?.toLowerCase() != "pending_approval" &&
          action == "edit") ||
        action == "view"
      ) {
        form.reset({
          [BUDGET_ID]: budgetData?.data?.[BUDGET_ID] || "",
          [BUDGET_NAME]: budgetData?.data?.[BUDGET_NAME] || "",
          [VERSION_NO]: budgetData?.data?.[VERSION_NO]?.toString() || "",
          [YEAR]: budgetData?.data?.[YEAR]?.toString() || "",
          [TOTAL_ANNUAL_BUDGET]:
            budgetData?.data?.[TOTAL_ANNUAL_BUDGET]?.toString() || "0",
          [TOTAL_APPROVALS]: budgetData?.data?.[TOTAL_APPROVALS] || "",
          [NOTES]: budgetData?.data?.[NOTES] || "",
          [LAST_UPDATED_BY]:
            budgetData?.data?.[LAST_UPDATED_BY]?.toString() || "",
          [LAST_UPDATED_ON]:
            moment(budgetData?.data?.[LAST_UPDATED_ON]).format("MM-DD-YYYY") ||
            "",
          [LINE_ITEMS_COUNT]:
            budgetData?.data?.[LINE_ITEMS_COUNT]?.toString() || "0",
        });
        setYearKey(Math.random() * 4);
        setVersionKey(Math.random() * 2);
      } else {
        toast.error(
          "This budget cannot be edited as its approval is still pending.",
          {
            className: "!bg-yellow-600 !text-white",
          }
        );
        onBackClick();
      }
    }
  }, [budgetData, action]);

  const invalidateBudgetData = () => {
    queryClient.invalidateQueries({
      queryKey: ["get-budget-data", communityId],
    });
    queryClient.invalidateQueries({
      queryKey: ["get-bom-budget-data", communityId],
    });
  };

  const isArchived = useMemo(() => budgetData?.data?.archived, [budgetData]);

  const { mutate: addInfo, isPending: isAddPending } = useAddBudgetMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      if (approvalClicked) {
        submitApproval({
          communityId: communityId as string,
          budgetId: budgetId as string,
          reason: "Submitted for Approval",
        });
      } else {
        invalidateBudgetData();
        onBackClick();
        toast.success("Budget Added Successfully");
      }
    },
  });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateBudgetMutation({
      onError: (error?: any) => {
        setApprovalClicked(false);
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        if (approvalClicked) {
          submitApproval({
            communityId: communityId as string,
            budgetId: budgetId as string,
            reason: "Submitted for Approval",
          });
        } else {
          invalidateBudgetData();
          onBackClick();
          toast.success("Budget Updated Successfully");
        }
      },
    });

  const { mutate: submitApproval, isPending: isSubmitApprovalPending } =
    useSubmitForApprovalMutation({
      onError: (error?: any) => {
        setApprovalClicked(false);
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setApprovalClicked(false);
        invalidateBudgetData();
        onBackClick();
        toast.success("Budget Submitted for Approval");
      },
    });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const budgetListPath = `/${routes.bom}/${routes.finance}/${routes.budgetApprovals}`;

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: "Activity Dashboard",
      },
      {
        link: budgetListPath,
        name: <span>{"Budget Management"}</span>,
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            {isAdd
              ? "Add Budget"
              : isView
              ? `View Budget - ${formData[BUDGET_NAME]}`
              : `Edit Budget - ${formData[BUDGET_NAME]}`}
          </span>
        ),
      },
    ],
    [formData, budgetListPath]
  );

  const onBackClick = () => {
    navigate(budgetListPath);
  };

  const onSubmit = (data: any) => {
    if (
      isView ||
      (isAdd ? !hasAddPermission : !approvalClicked && !hasUpdatePermission) ||
      (budgetId && budgetData?.data?.status?.toLowerCase() == "pending_approval")
    ) return;

    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (budgetId) {
      updateInfo({
        communityId: communityId as string,
        budgetId,
        data: { ..._data, communityId },
      });
    } else {
      delete _data[BUDGET_ID];
      addInfo({
        communityId: communityId as string,
        data: { ..._data, communityId },
      });
    }
  };

  const validateBudgetApproval = () => {
      const activeBomCount = (bomUsersList?.data || []).filter(
        (bom: any) => bom?.active
      ).length;
      const requiredApprovals = Number(
        communityApprovalInfo?.data?.budgetApprovals || 0
      );

      if (activeBomCount < requiredApprovals) {
        toast.error(
          "You can't submit this budget for approval because approvals are greater than active BM members."
        );
        return false;
      }

      return true;
  };

  const onSubmitApprovalBtnClick = () => {
    if (Number(form.getValues(LINE_ITEMS_COUNT)) > 0) {
      if (!validateBudgetApproval()) return;
      setOpenApprovalDialog(true);
    } else {
      toast.error(
        "You can't submit this budget for approval because it has no line items."
      );
    }
  };

  const onSubmitApproval = form.handleSubmit(
    (data) => {
      if (!hasSubmitPermission || isUpdatePending || isSubmitApprovalPending) return;
      if (!validateBudgetApproval()) return;
      setApprovalClicked(true);
      onSubmit(data);
    },
    () => setOpenApprovalDialog(false)
  );

  return {
    breadcrumbData,
    onBackClick,
    isView,
    isAdd,
    onSubmit,
    form,
    requiredFields,
    onCancel: onBackClick,
    isAddPending,
    isUpdatePending,
    isBudgetDataLoading,
    budgetId,
    yearKey,
    commentsData,
    isCommentsLoading,
    onSubmitApprovalBtnClick,
    onSubmitApproval,
    isSubmitApprovalPending,
    openApprovalDialog,
    setOpenApprovalDialog,
    versionKey,
    hasAddPermission,
    hasUpdatePermission,
    hasSubmitPermission,
    isArchived,
    formData,
  };
};

export default useAddEditBudget;
